import {useQuery} from '@tanstack/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';

import {getHealthAvailableTimes} from '../../services/getHealthAvailableTimes';

import {EntryPointCodeEnum} from 'presentation/constants/entryPointCodeEnum';
import {ShiftTypeEnum} from 'presentation/modules/booking/exam/constants/shiftTypeEnum';
import {useQueryParams} from 'presentation/pages';
import {useBookingStore} from '../../store/useBookingStore';
import {AvailableTimeItem, UseGetHealthMultiAvailableTimesQueryProps} from './types';
import {HEALTH_AVAILABLE_TIMES_STALE_TIME} from '../useGetHealthAvailableTimesQuery/constants';
import {TELEMEDICINE_INFO} from '../../constants/telemedicineInfo';

export function useGetHealthMultiAvailableTimesQuery({
	isTelemedicine,
	enabled,
	dates,
}: UseGetHealthMultiAvailableTimesQueryProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const {
		SEARCH_PARAMS: {shift, autocompleteQuery},
	} = useQueryParams();

	const availableTimesParams = {
		dateAt: bookingData.dateAt,
		unitCode: isTelemedicine ? TELEMEDICINE_INFO.code : bookingData.unitCode,
		shift: shift as ShiftTypeEnum,
		specialtyCode: bookingData.specialtyCode,
		subSpecialtyCode: bookingData.subSpecialtyCode,
		providerId:
			(bookingData?.isFavoriteDoctorsFlow && bookingData?.professionalCode) ||
			autocompleteQuery,
	};

	return useQuery({
		queryKey: [QUERY_KEYS.HEALTH_AVAILABLE_TIMES, availableTimesParams, dates],
		queryFn: async ({signal}) => {
			const firstThreeDates = dates?.slice(0, 3) ?? [];
			const availableTimes: PromiseSettledResult<AvailableTimeItem[]>[] =
				await Promise.allSettled(
					firstThreeDates?.map(date =>
						getHealthAvailableTimes({
							clinic_code: availableTimesParams.unitCode,
							specialty_code: availableTimesParams.specialtyCode,
							entryPointCode: Number(EntryPointCodeEnum.V2),
							subspecialty_code: availableTimesParams.subSpecialtyCode,
							date,
							shift_type: availableTimesParams.shift,
							providerCode: availableTimesParams.providerId,
							signal,
						}),
					),
				);

			const formattedAvailableTimes = availableTimes
				.map((result, index) => ({
					date: firstThreeDates[index],
					times: result.status === 'fulfilled' ? result.value : [],
				}))
				.filter(item => item.times.length > 0);

			return formattedAvailableTimes;
		},
		enabled,
		staleTime: HEALTH_AVAILABLE_TIMES_STALE_TIME,
	});
}
